import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Col,
  ErrorSummary,
  FormInput,
  FormRadioGroup,
  FormSelect,
  H3,
  InPageAlert,
  Row,
  SectionPage,
} from '@snsw/react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, validateExt } from 'shared/utils/Validation';

import { radioOptionsYesNo } from 'shared/constants/constants';
import ScrollToTop from 'shared/utils/scrollToTop';
import styles from './PlantItemDetails.module.scss';
import { RefData } from 'plantItemNew/constants/refData';
import { PagePath } from 'shared/constants/pagePath';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { saveApplication } from 'shared/redux/applicationSlice';
import { storeAssociatedDesign } from 'plantItemNew/redux/actions';
import { TopPageSummary } from 'shared/components/topPageSummary/TopPageSummary';
import { SaveOrCancelItem } from './SaveOrCancelItem';

export const AssociatedDesignRegistration = (props) => {
  ScrollToTop();

  const { index } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedPlantItems = useSelector((state) => state.plantItems);
  const refData = useSelector((state) => state.referenceData.data);
  const [designedBeforeRegistrationRequirement, setDesignedBeforeRegistrationRequirement] = useState(null);
  const [registeredDesignState, setRegisteredDesignState] = useState('');
  const [designRegistrationNumber, setDesignRegistrationNumber] = useState('');

  const [currentItemObject, setCurrentItemObject] = useState();

  const [designRequiredDateLabel, setDesignRequiredDateLabel] = useState('');

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const registrationState =
    refData && Array.isArray(refData[RefData.registrationStateOptions])
      ? refData[RefData.registrationStateOptions].map((e) => ({
          value: e.value,
          text: e.description,
        }))
      : [];

  useEffect(() => {
    if (designedBeforeRegistrationRequirement) {
      setRegisteredDesignState('');
      setDesignRegistrationNumber('');
    }

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [designedBeforeRegistrationRequirement, registeredDesignState, designRegistrationNumber]);

  useEffect(() => {
    if (Number.isInteger(+index) && savedPlantItems.items[index]) {
      let itemObject = savedPlantItems.items[index];

      //prevent user to access the page if plant item details not completed
      if (!itemObject?.itemDetails?.isCompleted) navigate.goBack();

      setCurrentItemObject(itemObject);

      if (itemObject?.designDetails?.designedBeforeRegistrationRequirement) {
        setDesignedBeforeRegistrationRequirement(
          itemObject?.designDetails?.designedBeforeRegistrationRequirement === 'yes',
        );
      }

      setRegisteredDesignState(itemObject?.designDetails?.state);
      setDesignRegistrationNumber(itemObject?.designDetails?.registrationNumber);

      if (itemObject?.itemDetails?.itemType === RefData.plantItemType.concretePlacingBoom) {
        setDesignRequiredDateLabel('');
      } else {
        setDesignRequiredDateLabel(PlantItemDetailsPage.associatedDesign.designedBeforeDateLabel);
      }
    }
  }, [savedPlantItems, index]);

  const validators = [
    {
      fieldName: 'designedBeforeRegistrationRequirement',
      fieldValue: designedBeforeRegistrationRequirement,
      isValid: (v) => typeof v === 'boolean',
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'registeredDesignState',
      fieldValue: registeredDesignState,
      isValid: (v) =>
        !isEmpty(v) ||
        designedBeforeRegistrationRequirement === true ||
        typeof designedBeforeRegistrationRequirement !== 'boolean', //to hide error if design date has not been selected
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'designRegistrationNumber',
      fieldValue: designRegistrationNumber,
      isValid: (v) =>
        !isEmpty(v) ||
        designedBeforeRegistrationRequirement === true ||
        typeof designedBeforeRegistrationRequirement !== 'boolean', //to hide error if design date has not been selected
      errorMessage: () => 'Please enter details',
    },
  ];

  const getItemObject = () => {
    let currentComponentValid = validateExt(validators, setErrors, setErrorSummary);

    let associatedDesign = {
      isCompleted: currentComponentValid,
      designedBeforeRegistrationRequirement: designedBeforeRegistrationRequirement ? 'yes' : 'no',
      state: registeredDesignState,
      registrationNumber: designRegistrationNumber,
    };

    return associatedDesign;
  };

  const saveAssociatedDesign = (associatedDesign, index) => {
    dispatch(storeAssociatedDesign(associatedDesign, index));
    dispatch(saveApplication(true));
  };

  const onSaveClick = () => {
    let associatedDesign = getItemObject();
    saveAssociatedDesign(associatedDesign, index);
    navigate(PagePath.plantItemList);
  };

  const onContinueClick = () => {
    let associatedDesign = getItemObject();
    if (associatedDesign.isCompleted) {
      saveAssociatedDesign(associatedDesign, index);
      navigate(`${PagePath.relatedItemDetails}/${index}`);
    } else {
      setShowErrors(true);
    }
  };

  const summmaryTitlePropertyMap = [
    {
      title: 'Plant item details',
      itemName: 'itemDetails',
      link: `${PagePath.plantDetails}/${index}`,
    },
  ];

  const detailLabelPropertyMap = [
    {
      title: 'Plant item:',
      itemPropertyName: 'itemTypeText',
    },
    {
      title: 'Type:',
      itemPropertyName: 'subType',
    },
    {
      title: 'Manufacturer:',
      itemPropertyName: 'manufacturer',
    },
    {
      title: 'Serial number:',
      itemPropertyName: 'serialNumber',
    },
    {
      title: 'Manufacturers model name:',
      itemPropertyName: 'modelNameOrNumber',
    },
    {
      title: 'Year of manufacture:',
      itemPropertyName: 'yearOfManufacture',
    },
    {
      title: 'Specific location of the item:',
      itemPropertyName: 'specificLocation',
    },
    {
      title: 'Unique identifier:',
      itemPropertyName: 'uniqueIdentifier',
    },
  ];

  return currentItemObject ? (
    <>
      <SectionPage>
        <TopPageSummary
          titlePropertyMap={summmaryTitlePropertyMap}
          detailPropertyMap={detailLabelPropertyMap}
          item={currentItemObject}
        />
      </SectionPage>
      <SectionPage>
        <ErrorSummary errors={errorSummary} />
        <H3>Associated design details</H3>
        <Row>
          <Col span={8}>
            <InPageAlert variant="info" title="Help:" headingLevel="h4">
              <p>
                If you do not know information about the design registration or item details, please contact the
                manufacturer, importer, supplier or the person who inspects your item for this information.
              </p>
            </InPageAlert>

            {currentItemObject?.itemDetails?.itemType === RefData.plantItemType.concretePlacingBoom && (
              <>
                <div className={styles.labelMsg}>
                  {PlantItemDetailsPage.associatedDesign.concretePlacingBoomDesignBeforeDateLabel}
                </div>

                <div className={styles.helpMsg}>
                  {PlantItemDetailsPage.associatedDesign.concreatePlacingBoomDesignBeforeDateFixedLabel} <br />
                  {PlantItemDetailsPage.associatedDesign.concreatePlacingBoomDesignBeforeDateMobileLabel}
                </div>
              </>
            )}

            <FormRadioGroup
              id="designedBeforeRegistrationRequirement"
              name="designedBeforeRegistrationRequirement"
              vertical={true}
              options={radioOptionsYesNo}
              onChange={(v) => {
                v === 'yes'
                  ? setDesignedBeforeRegistrationRequirement(true)
                  : setDesignedBeforeRegistrationRequirement(false);
              }}
              value={
                typeof designedBeforeRegistrationRequirement === 'boolean'
                  ? designedBeforeRegistrationRequirement
                    ? 'yes'
                    : 'no'
                  : ''
              }
              errorMessage={errors?.designedBeforeRegistrationRequirement?.errorMessage}
              hasError={showErrors && errors?.designedBeforeRegistrationRequirement?.invalid}
              legend={designRequiredDateLabel}
            />

            {designedBeforeRegistrationRequirement === false && (
              <>
                <FormSelect
                  label={PlantItemDetailsPage.associatedDesign.designRegisteredStateLabel}
                  errorMessage={errors?.registeredDesignState?.errorMessage}
                  hasError={showErrors && errors?.registeredDesignState?.invalid}
                  id="registeredDesignState"
                  options={registrationState}
                  placeholder="Please select"
                  onChange={(e, { value }) => setRegisteredDesignState(value)}
                  value={registeredDesignState}
                  inputWidth="md"
                  margin={{
                    top: 'lg',
                  }}
                />

                <FormInput
                  label={PlantItemDetailsPage.associatedDesign.designRegistrationNumberLabel}
                  helpMessage="Enter the full design registration number including all characters without any spaces."
                  id="designRegistrationNumber"
                  errorMessage={errors?.designRegistrationNumber?.errorMessage}
                  hasError={showErrors && errors?.designRegistrationNumber?.invalid}
                  value={designRegistrationNumber}
                  onChange={(e, { value }) => setDesignRegistrationNumber(value)}
                  inputWidth="lg"
                />
              </>
            )}
          </Col>
        </Row>
      </SectionPage>
      <SectionPage className={styles.marginTopThreeHalfRem}>
        <H3>Related details</H3>
        <Button className={styles.marginTopOneHalf} onClick={onContinueClick}>
          Next
        </Button>
      </SectionPage>

      <SaveOrCancelItem onSaveClick={onSaveClick} index={index} />
    </>
  ) : null;
};
